<template>
  <v-container fluid class="bom-container">
    <v-form ref="bomForm" @submit.prevent="saveBom">
      <v-row>
        <v-col cols="12" md="3">
          <v-expansion-panels v-model="informationPanel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                BOM details
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="pa-2">
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="form.productId"
                      :items="productOptions"
                      :item-title="productLabel"
                      item-value="product_id"
                      variant="outlined"
                      density="compact"
                      bg-color="white"
                      color="primary"
                      :rules="requiredRules"
                      autocomplete="off"
                      clearable
                      @update:model-value="onProductChange"
                    >
                      <template #label>
                        Product <span class="text-red-accent-4">*</span>
                      </template>
                    </v-autocomplete>

                    <v-textarea
                      v-model="form.description"
                      label="Description"
                      variant="outlined"
                      density="compact"
                      bg-color="white"
                      color="primary"
                      rows="3"
                      autocomplete="off"
                      clearable
                    />

                    <v-select
                      v-model="form.status"
                      :items="statusOptions"
                      label="Status"
                      variant="outlined"
                      density="compact"
                      bg-color="white"
                      color="primary"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12" md="9">
          <v-expansion-panels v-model="materialsPanel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Raw materials
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="pt-2" align="start">
                  <v-col cols="12" lg="8">
                    <v-autocomplete
                      v-model="selectedMaterialId"
                      :items="rawMaterialOptions"
                      :item-title="productLabel"
                      item-value="product_id"
                      label="Search Raw Material"
                      variant="outlined"
                      density="compact"
                      bg-color="white"
                      color="primary"
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" lg="4">
                    <v-btn
                      color="primary"
                      variant="flat"
                      prepend-icon="mdi-plus"
                      :disabled="!selectedMaterialId"
                      @click="addMaterial"
                    >
                      Add material
                    </v-btn>
                  </v-col>
                </v-row>

                <v-table density="compact" fixed-header height="40vh" class="bom-table">
                  <thead>
                    <tr>
                      <th class="text-center">SL</th>
                      <th class="text-center">Product Code</th>
                      <th class="text-left">Product Name</th>
                      <th class="text-center">Unit</th>
                      <th class="text-left quantity-column">Quantity</th>
                      <th class="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(material, index) in materials" :key="material.product_id">
                      <td class="text-center">{{ index + 1 }}</td>
                      <td class="text-center">{{ material.product?.code || material.code || '—' }}</td>
                      <td>{{ material.product?.name || material.name }}</td>
                      <td class="text-center">{{ material.product?.unit?.name || material.unit?.name || '—' }}</td>
                      <td>
                        <v-text-field
                          v-model.number="material.quantity"
                          :aria-label="'Quantity for ' + (material.product?.name || material.name)"
                          type="number"
                          min="0.01"
                          step="0.01"
                          variant="outlined"
                          density="compact"
                          bg-color="white"
                          color="primary"
                          hide-details
                        />
                      </td>
                      <td class="text-center">
                        <v-btn
                          icon="mdi-delete-outline"
                          :aria-label="'Remove ' + (material.product?.name || material.name)"
                          color="error"
                          variant="text"
                          size="small"
                          @click="removeMaterial(index)"
                        />
                      </td>
                    </tr>
                    <tr v-if="materials.length === 0">
                      <td colspan="6" class="text-center empty-items">
                        <v-icon icon="mdi-package-variant-closed" size="32" class="mb-2" />
                        <strong>No materials added yet</strong>
                        <span>Search for a raw material above to add it to this BOM.</span>
                      </td>
                    </tr>
                  </tbody>
                </v-table>

                <v-divider class="mt-5" />
                <v-card-actions class="justify-end flex-wrap ga-3 px-0 pt-5">
                  <v-btn color="primary" variant="tonal" :to="{ name: 'production-bom-records' }">
                    BOM Records
                  </v-btn>
                  <v-btn class="bg-grey-darken-1" @click="resetForm">Reset</v-btn>
                  <v-btn color="primary" variant="flat" type="submit">Save BOM</v-btn>
                </v-card-actions>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { toast } from "vue3-toastify";

export default {
  name: "AddManufacturingBom",
  data() {
    return {
      informationPanel: 0,
      materialsPanel: 0,
      form: {
        productId: null,
        description: "",
        status: 1,
      },
      selectedMaterialId: null,
      materials: [],
      statusOptions: [
        { title: "Active", value: 1 },
        { title: "Inactive", value: 0 },
      ],
      requiredRules: [(value) => !!value || "Product is required"],
    };
  },
  computed: {
    productOptions() {
      return (this.$store.getters.getProductMapList || []).filter(
        (item) => item.status === 1 || item.status === "1"
      );
    },
    rawMaterialOptions() {
      const selectedIds = new Set(this.materials.map((item) => item.product_id));
      return this.productOptions.filter(
        (item) => item.product_id !== this.form.productId && !selectedIds.has(item.product_id)
      );
    },
  },
  created() {
    if (!this.$store.getters.getProductMapList?.length) {
      this.$store.dispatch("getProductMapList");
    }
  },
  methods: {
    productLabel(item) {
      const product = item?.product || item || {};
      return [product.code, product.name].filter(Boolean).join(" — ");
    },
    onProductChange() {
      this.materials = this.materials.filter(
        (material) => material.product_id !== this.form.productId
      );
    },
    addMaterial() {
      const material = this.productOptions.find(
        (item) => item.product_id === this.selectedMaterialId
      );

      if (!material) return;

      this.materials.push({ ...material, quantity: 1 });
      this.selectedMaterialId = null;
    },
    removeMaterial(index) {
      this.materials.splice(index, 1);
    },
    async resetForm() {
      this.form = { productId: null, description: "", status: 1 };
      this.selectedMaterialId = null;
      this.materials = [];
      await this.$refs.bomForm?.resetValidation();
    },
    async saveBom() {
      const validation = await this.$refs.bomForm.validate();
      if (!validation.valid) return;

      if (!this.materials.length) {
        toast.error("Please add at least one raw material");
        return;
      }

      if (this.materials.some((material) => !material.quantity || material.quantity <= 0)) {
        toast.error("Raw material quantity must be greater than zero");
        return;
      }

      toast.info("The BOM form is ready for backend integration");
    },
  },
};
</script>

<style scoped>
.bom-container { padding: 24px; }
.bom-table { min-height: 280px; }
.quantity-column { min-width: 120px; width: 150px; }
.empty-items { height: 220px; color: #748b86; }
.empty-items strong, .empty-items span { display: block; }
.empty-items strong { margin-bottom: 6px; color: #3b6059; font-size: 15px; }
.empty-items span { font-size: 13px; }
@media (max-width: 960px) {
  .bom-container { padding: 12px; }
}
</style>
